import { useEffect, useRef, useState } from "react";
import { SimliAvatarClient } from "../lib/simli";

interface SimliAvatarProps {
  onReady: (client: SimliAvatarClient) => void;
  onError?: (err: string) => void;
}

const MAX_RECONNECT_ATTEMPTS = 8;

export function SimliAvatar({ onReady, onError }: SimliAvatarProps): JSX.Element {
  const videoRef = useRef<HTMLVideoElement>(null);
  const audioRef = useRef<HTMLAudioElement>(null);
  const callbacksRef = useRef({ onReady, onError });
  callbacksRef.current = { onReady, onError };
  const [reconnecting, setReconnecting] = useState(false);

  useEffect(() => {
    const videoEl = videoRef.current;
    const audioEl = audioRef.current;
    if (!videoEl || !audioEl) return;

    let cancelled = false;
    let currentClient: SimliAvatarClient | null = null;
    let attempt = 0;
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
    let connectInFlight = false;

    const scheduleReconnect = (reason: string): void => {
      if (cancelled) return;
      if (connectInFlight) return;
      attempt += 1;
      if (attempt > MAX_RECONNECT_ATTEMPTS) {
        callbacksRef.current.onError?.(`avatar_disconnected: ${reason}`);
        return;
      }
      const delayMs = Math.min(15000, 1000 * 2 ** (attempt - 1));
      setReconnecting(true);
      reconnectTimer = setTimeout(() => {
        void connect();
      }, delayMs);
    };

    const connect = async (): Promise<void> => {
      if (cancelled || connectInFlight) return;
      connectInFlight = true;
      try {
        if (currentClient) {
          await currentClient.close();
          currentClient = null;
        }
        const client = new SimliAvatarClient();
        await client.init({
          videoEl,
          audioEl,
          callbacks: {
            onConnected: () => {
              attempt = 0;
              setReconnecting(false);
            },
            onDisconnected: () => {
              if (cancelled) return;
              scheduleReconnect("disconnected");
            },
            onFailed: (r) => {
              if (cancelled) return;
              scheduleReconnect(r ?? "failed");
            },
          },
        });
        if (cancelled) {
          await client.close();
          return;
        }
        currentClient = client;
        callbacksRef.current.onReady(client);
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        if (attempt === 0) {
          callbacksRef.current.onError?.(msg);
        }
        scheduleReconnect(msg);
      } finally {
        connectInFlight = false;
      }
    };

    void connect();

    return () => {
      cancelled = true;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      void currentClient?.close();
    };
  }, []);

  return (
    <div className="w-full h-full flex items-center justify-center bg-black relative">
      <video
        ref={videoRef}
        autoPlay
        playsInline
        muted
        className="w-full h-full object-cover"
      />
      <audio ref={audioRef} autoPlay />
      {reconnecting && (
        <div className="absolute inset-0 flex items-center justify-center bg-ink-900/70 backdrop-blur-sm">
          <div className="flex flex-col items-center gap-2 text-white">
            <div className="w-8 h-8 border-2 border-ink-600 border-t-lime-400 rounded-full animate-spin" />
            <div className="text-xs uppercase tracking-widest">Reconnecting…</div>
          </div>
        </div>
      )}
    </div>
  );
}
